import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import axios from "axios";
import { Info, ChevronRight, ChevronDown } from "lucide-react";
import { fmtCurrency, fmtPct } from "../lib/formatters";
import { getCachedMethods, setCachedMethods } from "../lib/methodsCache";
import ComparativeAnalysisChart from "../components/ComparativeAnalysisChart";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Small info-icon that reveals a tooltip on hover / focus / click.
function InfoPop({ title, body, testid, side = "right" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // click-outside to dismiss
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        aria-label={`About ${title}`}
        data-testid={testid}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="flex items-center justify-center text-white hover:text-[#88BFDE]"
        style={{
          width: 16, height: 16, borderRadius: 999,
          background: "rgba(255,255,255,0.22)", cursor: "help",
        }}
      >
        <Info size={10} strokeWidth={2.4} />
      </button>
      {open && (
        <div
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          data-testid={`${testid}-popover`}
          className="absolute z-40"
          style={{
            top: "calc(100% + 6px)",
            [side]: -8,
            width: 340,
            maxWidth: "80vw",
            background: "#fff",
            border: "1px solid #1C4E80",
            boxShadow: "0 12px 30px rgba(28,78,128,0.20)",
            padding: "10px 12px",
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--u-ink)",
            fontStyle: "italic",
            fontFamily: "Source Sans 3",
            textAlign: "left",
            letterSpacing: 0,
            textTransform: "none",
            // Kill nowrap inherited from `.u-table th`
            whiteSpace: "normal",
            wordWrap: "break-word",
            overflowWrap: "break-word",
          }}
        >
          <div
            className="mb-1 font-display not-italic text-[13px] text-navy"
            style={{
              letterSpacing: 0,
              textTransform: "none",
              whiteSpace: "normal",
              wordWrap: "break-word",
            }}
          >
            {title}
          </div>
          {body}
        </div>
      )}
    </span>
  );
}

// Skeleton row shown while /methods is in flight and we have no
// cached response yet. Renders a subtle navy-tinted pulse in each
// cell so the tab never looks blank between click and paint.
// 2026-02-16 (Dave) — perceived-load polish. Actual wall time
// unchanged; the screen just never appears empty.
function SkeletonRows({ colCount, rowCount = 12 }) {
  return (
    <>
      {Array.from({ length: rowCount }).map((_, i) => (
        <tr
          key={`skeleton-${i}`}
          data-testid={`skeleton-row-${i}`}
          style={{ background: i % 2 === 1 ? "var(--u-sky-wash)" : undefined }}
        >
          {Array.from({ length: colCount }).map((__, j) => (
            <td
              key={j}
              style={{
                padding: "8px 12px",
                borderRight: j === colCount - 1 ? undefined : undefined,
              }}
            >
              <span
                className="u-skeleton-cell"
                style={{
                  display: "block",
                  height: 12,
                  width: j === 0 ? 56 : "70%",
                  marginLeft: j === 0 ? 0 : "auto",
                  borderRadius: 2,
                  background: "linear-gradient(90deg, rgba(28,78,128,0.08) 0%, rgba(28,78,128,0.18) 50%, rgba(28,78,128,0.08) 100%)",
                  backgroundSize: "200% 100%",
                  animation: "u-skeleton-pulse 1.1s ease-in-out infinite",
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// Fixed layout widths. Year col is wider than the auto-layout previously
// used because we now split the table into two tables (scrollable year
// rows on top, fixed Total+summary block on bottom) that share a single
// <colgroup>. With `table-layout: fixed` the browser strictly obeys the
// colgroup widths — no more auto-growth to fit the "PEAK EXPENDITURES
// YEAR 2033" label. 200 px is enough to comfortably fit that label at
// font-size 10 without wrap.
const YEAR_COL_WIDTH = 200;

// Column spec (order matters — Full Funding is intentionally last).
const COLS = [
  {
    key: "target",
    label: "Target Method",
    // 2026-02-15 (Dave) — Column widths tightened uniformly by 40 px
    // per column (5 × 40 = 200 px total) so the 5-column table fits
    // Chrome + Safari at 1440/1366-wide desktops without a horizontal
    // scrollbar and without clipping the 5th (Full Funding) column.
    // Cell contents are 6-9 char currency strings ($1,857,283 max),
    // so the remaining ~150-185 px still leaves comfortable padding
    // + tabular-numeral breathing room.
    width: 210,
    tooltip:
      "uReserve's Target Method is used as the basis to calculate the Year 1 Contribution Amount. With the ability to adjust the Reserve Fund Target Level variable on your Dashboard, this method is the most flexible, allowing immediately available results useful as your board considers your community's risk profile ahead of your Reserve Fund budget decision.",
  },
  {
    key: "adjusted_target",
    label: "Adjusted Target Method",
    width: 220,
    tooltip:
      "The Adjusted Target Method allows the Target Method calculation to be increased or decreased by a user-defined percentage. Negative adjustments help moderate large first-year contribution increases, while positive adjustments provide additional funding for underfunded reserves. The adjustment is then phased out over the user-specified Adjustment Period (in years) configured on the Dashboard.",
    // 2026-09-14 (Dave) — Divider removed.  Column no longer flags
    // `dividerAfter`, so the blue vertical rule between Adjusted
    // Target Method and Threshold Method is gone table-wide.
  },
  {
    key: "threshold",
    label: "Threshold Method",
    width: 210,
    illustration: true,
    tooltip:
      "Illustration Only: The Threshold Method calculates the Year 1 Contribution Amount so that the reserve balance equals 100% of the Peak Reserve Expenditure amount in the year before it occurs.",
  },
  {
    key: "cashflow",
    label: "Cash Flow Method",
    width: 210,
    illustration: true,
    tooltip:
      "Illustration Only: The Cash Flow Method calculates a Year 1 Contribution Amount that prevents the reserve balance from going negative in any year.",
  },
  {
    key: "full",
    label: "Full Funding Method",
    width: 180,
    illustration: true,
    tooltip:
      "Illustration Only: The Full Funding Method calculates a Year 1 Contribution Amount that brings the reserve balance to 100% of the fully-funded reserve target by the end of the study horizon.",
  },
];

// 2026-02 (Dave) — Target Method column is hidden in the UI but the
// underlying data continues to flow through /methods so any downstream
// consumer (reports, other tabs) still sees it.  The Contributions
// table renders VISIBLE_COLS everywhere the eye is drawn; column
// widths shift left to fill the reclaimed space.
const VISIBLE_COLS = COLS.filter((c) => c.key !== "target");

// Renders the "For Illustration Only" band across the illustration columns.
function IllustrationBand({ cols }) {
  const firstIllustration = cols.findIndex((c) => c.illustration);
  if (firstIllustration < 0) return null;
  return (
    <tr data-testid="illustration-band">
      {/* Year column spacer — sticky-left so the frozen Year column
          continues cleanly through the illustration band. Opaque bg
          so scrolled year values from below don't bleed through.
          2026-02-26 (Dave) — IllustrationBand now lives inside the
          TOP rollforward table (a direct child of the outer h-scroll
          wrap, not nested in the year-scroll body). Native
          sticky-left works without the manual `translateX` counter
          that the nested year-rows still need. */}
      <td
        style={{
          position: "sticky",
          left: 0,
          background: "#fff",
          zIndex: 1,
        }}
      />
      {cols.map((c, i) => {
        if (i < firstIllustration) {
          // Empty spacer cell for non-illustration columns, but still
          // paint the light-blue divider after `dividerAfter` cols so
          // the vertical rule crosses this band uninterrupted.
          return (
            <td
              key={c.key}
              style={{
                borderRight: c.dividerAfter ? "1px solid #0091D5" : undefined,
              }}
            />
          );
        }
        if (i === firstIllustration) {
          return (
            <td
              key="band"
              colSpan={cols.length - firstIllustration}
              className="text-center"
              style={{
                color: "#0091D5",
                fontStyle: "italic",
                fontSize: 13,
                fontWeight: 700,
                paddingTop: 4,
                paddingBottom: 4,
              }}
            >
              For Illustration Only
            </td>
          );
        }
        return null;
      })}
    </tr>
  );
}

// Shared <colgroup> for the two-table split. The scrollable year-rows
// table on top and the fixed Total+summary block on the bottom must
// have byte-identical column widths so the eye can trace a value
// straight down from Year 30 → Total → Ending Balance. Using a shared
// colgroup + `table-layout: fixed` on both tables locks this.
function ColSpec() {
  return (
    <colgroup>
      <col style={{ width: YEAR_COL_WIDTH }} />
      {VISIBLE_COLS.map((c) => (
        <col key={c.key} style={{ width: c.width }} />
      ))}
    </colgroup>
  );
}

// 2026-02-16 (Dave · perf) — Memoized yearly `<tr>` for the Contributions
// pivot. Wrapping the row in `React.memo` prevents all 30 rows from
// re-rendering on unrelated parent state churn (chart-more menu toggle,
// horizon selector, container resize). Only the row whose own props
// actually change (`byKey` when the scenario/horizon flips, `isPeak`
// when peak year moves) repaints. Rows are structurally identical
// otherwise so shallow-compare is safe.
//
// Prop discipline (must all be stable across renders that shouldn't
// trigger a repaint):
//   * `yr`, `i`, `isPeak`, `striped` — plain primitives keyed on the
//     row; stable per-row identity.
//   * `byKey` — memoized in the parent via `useMemo(..., [methods])`
//     so the shallow reference only changes when a new /methods
//     response lands.
//   * `COLS`, `CELL_STYLE`, `fmtCurrency` — module-scope closures.
const YearRow = React.memo(function YearRow({ yr, i, isPeak, striped, byKey }) {
  // 2026-02-16 (Dave, coral-line polish) — inset shadows for coral
  // hair-weight rules on the peak row so `border-collapse` never
  // arbitrates against us.
  const peakShadow = isPeak
    ? "inset 0 1px 0 0 #EA6A47, inset 0 -1px 0 0 #EA6A47"
    : undefined;
  return (
    <tr
      data-testid={`row-year-${yr}`}
      style={{
        background: striped ? "var(--u-sky-wash)" : undefined,
      }}
    >
      <td
        className="contrib-year-sticky"
        style={{
          paddingLeft: 12,
          fontFamily: "IBM Plex Mono, monospace",
          fontWeight: isPeak ? 600 : 400,
          // 2026-02-16 (Dave, freeze-Year-col) — sticky-left so the
          // year label stays visible on iPad horizontal scroll.
          // `translateX(--outer-sx)` pins to the outer wrap's
          // viewport since year-scroll's own sticky scope doesn't
          // see the outer's scrollLeft.
          position: "sticky",
          left: 0,
          background: striped ? "var(--u-sky-wash)" : "#fff",
          zIndex: 1,
          transform: "translateX(var(--outer-sx, 0))",
          boxShadow: peakShadow,
        }}
      >
        {isPeak ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 0,
              fontWeight: 600,
            }}
          >
            <span>{yr}</span>
            <span
              className="text-coral font-semibold"
              style={{
                fontSize: 10,
                letterSpacing: 1,
                fontFamily: "Source Sans 3",
                lineHeight: 1,
                textTransform: "none",
              }}
              data-testid="peak-label"
            >
              {"\u00A0\u00A0"}Peak Expenditure Year
            </span>
          </span>
        ) : (
          <span>{yr}</span>
        )}
      </td>
      {VISIBLE_COLS.map((c) => {
        const m = byKey[c.key];
        const val = m?.schedule?.[i]?.contribution;
        return (
          <td
            key={c.key}
            data-testid={`cell-${c.key}-${yr}`}
            style={{
              ...CELL_STYLE,
              borderRight: c.dividerAfter ? "1px solid #0091D5" : undefined,
              paddingRight: c.dividerAfter ? 35 : CELL_STYLE.paddingRight,
              fontFamily: "IBM Plex Mono, monospace",
              fontVariantNumeric: "tabular-nums",
              fontWeight: isPeak ? 600 : 400,
              boxShadow: peakShadow,
            }}
          >
            {val === undefined ? "—" : fmtCurrency(val)}
          </td>
        );
      })}
    </tr>
  );
});

export default function ContributionsScreen({ association, variables, ffbByYear = null, horizon = 30, chartView = false, onDismissChart, onToggleChart }) {
  // 2026-03-04 (Dave · sync-render fix) — Read the /methods cache
  // SYNCHRONOUSLY via a lazy state initializer, so if App.js's
  // Dashboard preload already stashed a fresh entry for this
  // (aid, variables) tuple, the very first render already has data.
  // Prior implementation seeded `methods=null` and only populated it
  // inside a useEffect on mount, which meant even the "instant cache"
  // path burned one render cycle showing the empty-shell panel — the
  // exact staggered "1st column, then data cols + chart" flash the
  // board members were noticing. Now cached mounts paint labels, data
  // cells, AND the chart in a single commit.
  const [methods, setMethods] = useState(() => {
    if (!association?.aid || !variables) return null;
    return getCachedMethods(association.aid, variables) || null;
  });
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  // 2026-02-15 (Dave) — Retry counter. Bump to re-fire the /methods
  // fetch without changing association/variables. Useful when the user
  // hits transient 404s (e.g., another tab renames/deletes the
  // association mid-view, or a lingering stale token drops a request).
  const [retryTick, setRetryTick] = useState(0);
  // 2026-02-16 (Dave, freeze-Year-col) — measure the year-scroll body's
  // scrollbar width and mirror it as `padding-right` on the summary
  // block so both tables render at an identical inline width. This
  // preserves the pixel-perfect blue-divider alignment across the
  // scrolling year rows and the fixed Total/summary rows WITHOUT
  // wrapping the summary in its own overflow container — which would
  // (and previously did) kill iPad horizontal scroll by hiding the
  // 1230-px table width from the outer wrap. On overlay-scrollbar OSes
  // (macOS Safari) the measurement returns 0, so no gutter is added —
  // both tables are naturally aligned.
  const scrollBodyRef = useRef(null);
  // Chart overlay is anchored to the scroll body's top; reset any detail-view scroll so it renders in full.
  useLayoutEffect(() => {
    if (chartView && scrollBodyRef.current) scrollBodyRef.current.scrollTop = 0;
  }, [chartView]);
  const outerWrapRef = useRef(null);
  const [sbGutter, setSbGutter] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = scrollBodyRef.current;
      if (!el) return;
      setSbGutter(el.offsetWidth - el.clientWidth);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    if (scrollBodyRef.current) ro.observe(scrollBodyRef.current);
    return () => ro.disconnect();
  }, [methods, running]);

  // 2026-02-28 (Dave) — Dynamic max-height for the Annual
  // Contributions detail scroll body.  A hard-coded
  // `calc(100vh - Xpx)` gets stale the moment anything above it
  // grows (new summary row, dynamic label wrapping, etc.) — on
  // Dave's 800 px viewport the last ~3 year rows were being
  // pushed BELOW the browser viewport because the scroll body
  // itself extended past its bottom edge.
  //
  // Measure the scroll body's live `top` on layout + on resize
  // + on toolbar toggle, then cap its height to
  // `viewportHeight - top - safetyMargin`.  Result: the scroll
  // body always fits snugly within the viewport, its own
  // scrollbar can page through all 30 year rows, and the final
  // rows never disappear off the bottom of the page.
  const [scrollBodyMaxH, setScrollBodyMaxH] = useState(null);
  useEffect(() => {
    const compute = () => {
      const el = scrollBodyRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const safety = 24; // room for the app footer + a hair of padding
      const next = Math.max(200, Math.round(window.innerHeight - top - safety));
      setScrollBodyMaxH(next);
    };
    compute();
    window.addEventListener("resize", compute);
    // Also recompute whenever the summary rows above may have
    // grown/shrunk (new methods payload, chartView flip).
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(compute) : null;
    if (ro && scrollBodyRef.current?.parentElement) {
      ro.observe(scrollBodyRef.current.parentElement);
    }
    return () => {
      window.removeEventListener("resize", compute);
      if (ro) ro.disconnect();
    };
  }, [methods, running]);

  // 2026-02-16 (Dave, freeze-Year-col, iPad sync) — the year-scroll
  // body is a vertical scroll container, which scopes any
  // `position: sticky; left: 0` inside it to year-scroll's own
  // horizontal scroll position (which is always 0 — we don't
  // horizontally scroll inside the year-scroll). Meanwhile the outer
  // wrap DOES horizontally scroll on iPad. Result: without this hook,
  // the year rows' Year column slides off the left with outer scroll
  // while the summary rows' labels stay put — jarring inconsistency.
  //
  // Fix: pipe the outer wrap's live `scrollLeft` into a CSS custom
  // property `--outer-sx`, and translate the year-scroll's Year cells
  // by `translateX(var(--outer-sx))` so they appear pinned to the
  // outer's left edge — matching the summary block's native
  // sticky-left behaviour. Uses rAF to coalesce updates during
  // momentum scroll on trackpads/iPads.
  useEffect(() => {
    const outer = outerWrapRef.current;
    if (!outer) return;
    let raf = 0;
    const apply = () => {
      outer.style.setProperty("--outer-sx", `${outer.scrollLeft}px`);
      raf = 0;
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(apply);
    };
    outer.addEventListener("scroll", onScroll, { passive: true });
    apply();
    return () => {
      outer.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [methods]);

  useEffect(() => {
    if (!association?.aid || !variables) return;
    // Perf (2026-02-16) — cache hit path: App.js preloads /methods
    // silently while the user is looking at Dashboard. If a fresh
    // entry exists for this (aid, variables) tuple, paint instantly
    // and skip the round-trip entirely.
    const cached = getCachedMethods(association.aid, variables);
    if (cached) {
      setMethods(cached);
      setError(null);
      return;
    }
    setRunning(true);
    setError(null);
    const ctrl = new AbortController();
    axios
      .post(
        `${API}/associations/${association.aid}/methods`,
        { variables },
        { signal: ctrl.signal },
      )
      .then((res) => {
        setMethods(res.data.methods);
        // Populate the cache so a subsequent tab visit within the TTL
        // window is instant (e.g., user flips to Elements and back).
        setCachedMethods(association.aid, variables, res.data.methods);
      })
      .catch((e) => {
        if (e.name === "CanceledError") return;
        // 2026-02-15 (Dave · Contributions 404 rescue) — surface the
        // HTTP status + a plain-English hint so a user is never
        // stranded on a bare "Request failed" message. If the request
        // 404'd, it's almost always because the currently-selected
        // association isn't reachable — the association list on the
        // TopBar may have shifted (rename, tenant re-scope, or another
        // tab deleted the row). Nudge the app to re-sync via the same
        // event the FirmsScreen dispatches after an admin edit.
        //
        // 2026-02-19 (Dave) — 403 is a DIFFERENT beast: the association
        // exists but the current user doesn't have a membership scoped
        // to it. Common causes: signed in as a limited-scope user
        // (view-only for one association only) while the TopBar shows
        // a broader picker, OR left a stale `currentAid` in state
        // after switching accounts. Show a proper permission message
        // and dispatch the same re-sync event so Shell can auto-flip
        // `currentAid` to the first association the current user CAN
        // see. Prevents the "misleading renamed/deleted" copy that
        // sent Dave down the wrong debug path.
        const status = e.response?.status;
        let hint = "";
        if (status === 404) {
          hint = "The selected association may have been renamed or deleted — refreshing the list. Try picking it again.";
          try {
            window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
          } catch { /* noop */ }
        } else if (status === 403) {
          hint = "You don't have permission to view this association. Switching to one you can access…";
          try {
            window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
          } catch { /* noop */ }
        } else if (status === 401) {
          hint = "Your session expired. Sign out and back in.";
        } else if (status === 429) {
          hint = "Too many requests. Wait a moment and click Retry.";
        }
        const detail = e.response?.data?.detail;
        const parts = [e.message];
        if (status) parts.push(`(HTTP ${status})`);
        if (detail) parts.push(`— ${detail}`);
        if (hint) parts.push(`\n${hint}`);
        setError(parts.join(" "));
      })
      .finally(() => setRunning(false));
    return () => ctrl.abort();
  }, [association?.aid, variables, retryTick]);

  // Horizon-context-sensitive roll-forward summary (Dave, 2026-08-01).
  // The backend still returns full-30yr scalar totals on each method
  // (`total_contributions`, `ending_balance`, `funding_level_pct`, etc.).
  // For the summary block at the bottom of the tab we ignore those and
  // recompute per-method from the truncated schedule so the numbers
  // match what the user sees in the columns above.
  //
  //   Beginning Balance ...... always study Year 1 opening (m.beginning_balance)
  //   Contributions .......... Σ contribution over years 1..N
  //   Interest Earned ........ Σ interest       over years 1..N
  //   Special Assessments .... Σ special_assessment over years 1..N
  //   Expenditures ........... Σ expenditures  over years 1..N
  //   Ending Balance ......... schedule[N-1].ending
  //   Funding Level .......... schedule[N-1].ending  ÷  schedule[N-1].full_funding_balance × 100
  //                            (Dave's ruling: measured against fully-funded as of end of the
  //                             selected horizon period, not end of Year 30.)
  //
  // Falls back to the backend scalars if the schedule is missing / empty
  // (defensive; should never happen in practice).
  const summaryByKey = React.useMemo(() => {
    const out = {};
    if (!methods) return out;
    // Length used for the row window; matches the display column count.
    const target = methods.find((x) => x.key === "target");
    const scheduleLen = target?.schedule?.length || 0;
    const N = Math.min(horizon, scheduleLen);
    for (const m of methods) {
      const sch = m.schedule || [];
      const last = N > 0 && sch.length >= N ? sch[N - 1] : null;
      const S = m.display?.horizons?.[String(N)];
      if (!last || !S) {
        out[m.key] = m;
        continue;
      }
      // Horizon-window totals, Funding %, signed target (Shortfall) Surplus and low balance
      // are server-computed (methods[].display.horizons[N]).
      out[m.key] = {
        ...m,
        beginning_balance: m.beginning_balance, // always Year-1 opening
        total_contributions: S.total_contributions,
        total_interest: S.total_interest,
        total_special_assessments: S.total_special_assessments,
        total_expenditures: S.total_expenditures,
        ending_balance: S.ending,
        fully_funded_balance: S.full_funding,
        target_funded_balance: S.target_balance,
        funding_level_pct: S.funding_pct,
        funding_level_shortfall: S.surplus,
        low_balance_year: S.low_balance_year,
        low_balance_amount: S.low_balance_amount,
      };
    }
    return out;
  }, [methods, horizon]);

  // 2026-02-16 (perf) — memoize `byKey` so the shallow-compared
  // YearRow / SummaryRow children below don't rebuild every parent
  // render (opening a horizon selector, toggling a chip, etc.).
  // Prior implementation rebuilt this object every render, which
  // defeated React.memo on any child that consumed it.
  // Hook must be declared BEFORE the early-return so hook order
  // stays stable across renders.
  const byKey = React.useMemo(
    () => Object.fromEntries((methods || []).map((m) => [m.key, m])),
    [methods],
  );

  if (!association) return <div className="text-slate p-4">Loading…</div>;

  // 2026-03-04 (Dave · sync-render fix) — Hold the entire panel behind
  // a single loading state until /methods is available. Previously the
  // panel rendered its shell (label column + Year column + skeleton
  // rows) immediately on mount and then filled the numeric columns +
  // chart when the /methods response arrived. The visual effect was
  // "1st column, then chart and other columns pop in" — jarring.
  // Now nothing paints until we have data (from cache OR network),
  // so labels + numbers + chart all appear in the same commit.
  // Errors take precedence so the retry banner is reachable even
  // while nothing else has data.
  if (!methods && !error) {
    return (
      <div
        className="panel-compact flex flex-col flex-1 items-center justify-center"
        data-testid="screen-contributions-loading"
        style={{ minHeight: 240, color: "var(--slate)" }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
          <path d="M21 12a9 9 0 0 0-9-9">
            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
          </path>
        </svg>
        <div className="mt-2 text-[11px] font-mono tracking-wider uppercase opacity-70">Solving contribution methods…</div>
      </div>
    );
  }

  const target = byKey.target;
  const scheduleLen = target?.schedule?.length || 0;
  const displayLen = Math.min(horizon, scheduleLen);
  const years = target ? target.schedule.slice(0, displayLen).map((r) => r.year) : [];
  const peakYear = target?.peak_expenditure_year;

  // Horizon-context-sensitive roll-forward summary — see block below.

  // Right-aligned column cell style — Requirement #6
  const cellStyle = { textAlign: "right", paddingRight: 12 };
  const headerStyle = { textAlign: "right", paddingRight: 12 };

  return (
    <div
      className="panel-compact flex flex-col flex-1 overflow-hidden"
      data-testid="screen-contributions"
    >
      {error && (
        <div
          className="bg-coral-wash border border-coral px-3 py-2 text-[12px] text-ink mb-2 flex items-start justify-between gap-4"
          data-testid="methods-error"
        >
          <div className="whitespace-pre-line flex-1">{error}</div>
          <button
            type="button"
            onClick={() => setRetryTick((t) => t + 1)}
            className="text-[11px] font-semibold text-navy border border-navy px-2.5 py-1 rounded-[3px] bg-white hover:bg-navy hover:text-white transition-colors shrink-0"
            data-testid="methods-retry-btn"
            disabled={running}
            style={{ cursor: running ? "not-allowed" : "pointer" }}
          >
            {running ? "Retrying…" : "Retry"}
          </button>
        </div>
      )}

      <div
        className="flex-1 flex flex-col overflow-x-auto"
        // 2026-09-14 (Dave) — Explicit `overflowY: hidden` here is
        // load-bearing.  Tailwind's `overflow-x-auto` sets only
        // `overflow-x: auto`, but per CSS spec the browser auto-
        // promotes the other axis from `visible` → `auto` whenever
        // one axis is `auto`.  That silent promotion was surfacing
        // a vertical scrollbar on this wrap at short viewport
        // heights (~700-750 px) when the chart overlay + summary
        // table together exceed the wrap's client height.  Pinning
        // overflowY to `hidden` keeps the scrollbar suppressed at
        // every viewport size while still letting the horizontal
        // scrollbar surface if the year table's columns overflow.
        style={{ minHeight: 0, overflowY: "hidden" }}
        data-testid="contributions-tables-wrap"
        ref={outerWrapRef}
      >
        {/* 2026-02-26 (Dave) — Layout swap. The rollforward summary
            (Beginning Balance → Funding Level Shortfall) now lives at
            the TOP of the panel, together with the column-header thead.
            The scrolling year rows sit below it, and the Total row is
            pinned at the very bottom. This pushes the (now compact)
            scrolling area DOWN, which reads much cleaner at 5-year
            horizon (previously ~500 px of dead space sat between the
            last year and the summary).

            Alignment: all three tables (top rollforward, middle year
            scroll, bottom total) share the same `<ColSpec />` colgroup
            and `table-layout: fixed`. Both fixed tables reserve the
            year-scroll's `sbGutter` on the right so their inline widths
            match the middle scroll pane on classic-scrollbar OSes.
            (macOS Safari overlay scrollbars → sbGutter=0, no-op.) */}

        {/* TOP · Column headers + Illustration band + Rollforward rows */}
        <div
          className="shrink-0"
          style={{ paddingRight: `${sbGutter}px` }}
        >
          <div style={{ borderTop: "2px solid #EA6A47", marginBottom: 6 }} data-testid="contributions-coral-rule" />
          <table
            className="u-table u-table-dense"
            data-testid="contributions-rollforward-table"
            style={{ tableLayout: "fixed", width: "100%", minWidth: "max-content" }}
          >
            <ColSpec />
            <thead>
              {/* 2026-02-28 (Dave, v2) — Panel title strip inside the
                  navy header.  Three-column layout: title LEFT,
                  year-range + peak-expenditure CENTER, Scenario
                  History pill RIGHT.  Replaces the outer white
                  ControlsStrip (association name is already in the
                  TopBar association picker, so we drop the redundant
                  "Demo Association A" text and reclaim the ~34 px
                  row).  Everything sits inside one <th colSpan> so
                  it aligns with the column-label row below with
                  zero white gap. */}
              <tr>
                <th
                  aria-hidden="true"
                  style={{ background: "#1C4E80", padding: "2px 12px", borderTopLeftRadius: 3 }}
                />
                <th
                  colSpan={VISIBLE_COLS.length}
                  data-testid="contributions-header-band"
                  style={{
                    background: "#1C4E80",
                    color: "#fff",
                    padding: "2px 12px",
                    borderBottom: "0.5px solid #fff",
                    borderTopRightRadius: 3,
                    textTransform: "none",
                    letterSpacing: "normal",
                    textAlign: "center",
                  }}
                >
                  {/* Title + year range + peak year, one string centered over the data columns. */}
                  <span className="font-bold" style={{ fontSize: 14 }} data-testid="contributions-header-title">
                    Funding Method Comparison
                    <span className="font-mono tabular-nums">
                      {" · "}{association.study_year} – {association.study_year + displayLen - 1}
                      {peakYear ? ` · Peak Expenditure: ${peakYear}` : ""}
                    </span>
                  </span>
                  {running && (
                    <span className="ml-3 text-[10.5px] font-mono tracking-wider uppercase opacity-70">· solving…</span>
                  )}
                </th>
              </tr>
              <tr>
                <th
                  data-testid="th-year"
                  style={{
                    position: "sticky", left: 0, background: "#1C4E80",
                    color: "#fff", textAlign: "left", paddingLeft: 12,
                    fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase",
                    zIndex: 2, verticalAlign: "middle", lineHeight: 1.2,
                  }}
                >
                  Reserve Account Rollforward
                </th>
                {VISIBLE_COLS.map((c) => {
                  // 2026-02 (Dave) — Adjusted Target column label
                  // becomes conditional on the Year-1 adjustment
                  // slider. When adjustment_pct ≠ 0 the column reads
                  // "Target Method - Adjusted" so boards know the
                  // number reflects a Year-1 nudge; when the slider
                  // sits at 0 there IS no adjustment, so we drop the
                  // suffix and the column simply reads "Target
                  // Method". Underlying data flow is unchanged.
                  const hasY1Adjustment =
                    Number(variables?.adjustment_pct || 0) !== 0;
                  const displayLabel =
                    c.key === "adjusted_target"
                      ? hasY1Adjustment
                        ? "Target Method - Adjusted"
                        : "Target Method"
                      : c.label;
                  return (
                    <th
                      key={c.key}
                      data-testid={`th-${c.key}`}
                      style={{
                        background: "#1C4E80",
                        color: "#fff",
                        borderRight: c.dividerAfter ? "1px solid #0091D5" : undefined,
                        ...headerStyle,
                        paddingRight: c.dividerAfter ? 35 : headerStyle.paddingRight,
                        fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase",
                      }}
                    >
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        <span>{displayLabel}</span>
                        <InfoPop
                          title={displayLabel}
                          body={c.tooltip}
                          testid={`info-${c.key}`}
                          side="right"
                        />
                      </span>
                    </th>
                  );
                })}
              </tr>
              {/* 2026-02-28 (Dave) — IllustrationBand deleted; real
                  estate is too precious.  The three illustration
                  columns keep their sky-shaded background so readers
                  still recognise them as illustrative. */}
            </thead>
            <tbody>
              {/* Rollforward summary — moved to top (was below Total). */}
              <SummaryRow
                label="Beginning Balance"
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => m.beginning_balance}
                bold
              />
              <SummaryRow
                label="Contributions"
                labelExtra={onToggleChart && (
                  <button
                    type="button"
                    onClick={onToggleChart}
                    className="inline-flex items-center text-navy hover:text-coral transition-colors align-middle ml-1"
                    title={chartView ? "Show the year-by-year Contributions detail" : "Hide the year-by-year detail and return to the chart"}
                    aria-expanded={!chartView}
                    data-testid="contrib-detail-toggle"
                  >
                    {chartView ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                  </button>
                )}
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => m.total_contributions}
                flowRule
                indent
              />
              <SummaryRow
                label="Interest Earned"
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => m.total_interest}
                indent
              />
              {/* 2026-02-28 (Dave) — Expenditures split into two
                  companion rows so the reader sees at a glance how
                  much of the year's outflow was covered by the
                  reserve pool vs. how much was covered by a Special
                  Assessment.
                    · From Reserves          = expenditures − special_assessments
                    · From Special Assessments = special_assessments
                  Both are rendered as accounting-negative
                  (parentheses, coral) so they read as expense-side
                  entries.  The standalone "Special Assessments"
                  income row is retired — the same figure now lives
                  in "Expenditures Funded From Special Assessments"
                  with the correct expense framing. */}
              <SummaryRow
                label="Expenditures Funded From Reserves"
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => -(m.total_expenditures - m.total_special_assessments)}
                colorful
                indent
              />
              <SummaryRow
                label="Expenditures Funded From Special Assessments"
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => -m.total_special_assessments}
                colorful
                indent
              />
              <SummaryRow
                label="Ending Balance"
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => m.ending_balance}
                bold
                flowRule
              />
              <SummaryRow
                label="Fully Funded Balance"
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => m.fully_funded_balance}
                indent
              />
              {/* 2026-02-16 (Dave) — Target Funding Level row: label
                  carries the current target %; value = fully_funded ×
                  target_pct. */}
              <SummaryRow
                label={`Target Funding Level at ${Math.round(((Number(variables?.target_pct) || 0)) * 100)}%`}
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => m.target_funded_balance}
                indent
              />
              <SummaryRow
                label="Funding Level %"
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => fmtPct(m.funding_level_pct, 0)}
                raw
                bold
              />
              <SummaryRow
                label="Target Funding Level (Shortfall) Surplus"
                cols={VISIBLE_COLS}
                byKey={summaryByKey}
                getter={(m) => m.funding_level_shortfall}
                bold
                variance
              />
            </tbody>
          </table>
        </div>

        {/* MIDDLE · Scrollable year rows. No thead now — column headers
            live in the top rollforward table above. Vertical scrollbar
            lives ONLY here so the wheel spins the study years without
            crossing the fixed rollforward above or the Total row below. */}
        {/* 2026-02-28 (Dave) — scroll body demoted to overflow-y:
            hidden.  With the compressed row heights (padding 1 px,
            font 11 px, line-height 1.1) the full 30-year horizon
            fits comfortably in the viewport, and the scrollbar the
            container previously reserved was just cosmetic chrome
            on the right edge of the panel.  Class flips
            `overflow-y-auto` → `overflow-y-hidden` so the container
            stops reserving room for a scrollbar entirely. */}
        <div
          className="shrink-0"
          style={{
            // 2026-02-28 (Dave, v3) — Removed the old
            // `minHeight: 470` for chart view — it fought the
            // dynamic maxHeight below and could push the panel
            // past the browser bottom on shorter viewports.  The
            // Comparative Illustration chart now inherits its
            // height from `scrollBodyMaxH` (viewport-fitting).
            //
            // 2026-02-28 (Dave, later) — At short horizons (5y, 10y)
            // the scroll body collapsed to fit the handful of year
            // rows behind the chart (~90 px), leaving the chart
            // overlay a sliver.  Pin `minHeight` to the same
            // measured viewport-fitting value we use for
            // `maxHeight`, so the panel is a fixed size regardless
            // of horizon.  At 30y with dense rows this equals the
            // maxHeight (no extra space); at 5y this means a few
            // rows sit at the top and the chart fills the full
            // viewport-height overlay above them — no collapse.
            minHeight: scrollBodyMaxH ? `${scrollBodyMaxH}px` : 0,
            // Same dynamic-max-height fit applies to both modes.
            // Reading the body's live top → cap keeps the whole
            // panel (border, plot, Low Reserve row) on screen at
            // any viewport.
            maxHeight: scrollBodyMaxH
              ? `${scrollBodyMaxH}px`
              : (chartView ? undefined : "calc(100vh - 420px)"),
            overflowX: "hidden",
            // 2026-02-28 (Dave) — Vertical scroll bar on the plain
            // "Annual Contributions" detail view (chartView=false).
            // Comparative Illustration view keeps overflow-y hidden
            // so the chart overlay perfectly covers the year rows
            // beneath it (no leaked scrollbar).  When the user
            // switches back to the detail view via the topbar
            // toggle, the schedule scrolls freely inside the panel
            // instead of the whole page.
            overflowY: chartView ? "hidden" : "auto",
            // 2026-02-28 (Dave, later) — `scrollbar-gutter: stable`
            // reserves the ~15 px scrollbar column at all times, so
            // the year-row data columns don't "shimmy" left/right
            // when toggling between the chart view (no scrollbar)
            // and the detail view (scrollbar present).  The gutter
            // is invisible in chart mode and gets filled by the
            // real scrollbar in detail mode — same width either
            // way.
            scrollbarGutter: "stable",
            position: "relative",
          }}
          data-testid="contributions-scroll-body"
          ref={scrollBodyRef}
        >
          <table
            className="u-table u-table-dense"
            data-testid="contributions-table"
            style={{ tableLayout: "fixed", width: "100%", minWidth: "max-content" }}
          >
            <ColSpec />
            <tbody>
              {(!methods && running) && (
                <SkeletonRows colCount={VISIBLE_COLS.length + 1} rowCount={12} />
              )}
              {/* 2026-03-06 (Dave) — Risk-band summary rows above the
                  annual contribution schedule. Always visible per
                  Dave's follow-up ("please eliminate the toggle
                  and have the 3 new risk bands show always"). Each
                  row counts how many years within the current
                  horizon fall in the given funding-level band, per
                  method. */}
              <RiskBandRow
                label="Low Risk Funding Years (#)"
                band="low"
                byKey={byKey}
                cols={VISIBLE_COLS}
                horizon={displayLen}
                testid="risk-band-low"
              />
              <RiskBandRow
                label="Moderate Risk Funding Years (#)"
                band="mod"
                byKey={byKey}
                cols={VISIBLE_COLS}
                horizon={displayLen}
                testid="risk-band-mod"
              />
              <RiskBandRow
                label="High Risk Funding Years (#)"
                band="high"
                byKey={byKey}
                cols={VISIBLE_COLS}
                horizon={displayLen}
                testid="risk-band-high"
              />
              {/* 2026-02 (Dave) — Average Funding Level % row:
                  arithmetic mean of per-year (ending / fully_funded)
                  across the visible horizon, clipped 0..100%.
                  2026-02-28 (Dave) — Full Funding Method column
                  added (originally scoped to Adjusted Target /
                  Threshold / Cash Flow only). */}
              <AvgFundingLevelRow
                label="Average Funding Level %"
                byKey={byKey}
                cols={VISIBLE_COLS}
                horizon={displayLen}
                shownFor={["adjusted_target", "threshold", "cashflow", "full"]}
                testid="avg-funding-level"
              />
              {years.map((yr, i) => (
                <YearRow
                  key={yr}
                  yr={yr}
                  i={i}
                  isPeak={yr === peakYear}
                  striped={i % 2 === 1}
                  byKey={byKey}
                />
              ))}
              {/* 2026-09-14 (Dave) — Total / Low Reserve Balance Year /
                  Low Balance Amount rows removed per Dave; the low-
                  reserve values now live in the Comparative Analysis
                  chart footer as "$X,XXX (YYYY)".  The underlying
                  computations in summaryByKey remain intact for any
                  future callers. */}
            </tbody>
          </table>
          {/* 2026-09-14 (Dave) — Chart overlay now uses top:0/bottom:0
              (fill parent) instead of a fixed height.  This guarantees
              the chart always covers every year row through the last
              (Year 30 / 2055 at 30y horizon) regardless of horizon —
              no more year-row peek beneath the panel.  Parent's own
              minHeight (470) still ensures the panel doesn't collapse
              on very short horizons where the year table is shorter
              than the chart's natural content. */}
          {chartView && (
            <div
              data-testid="comparative-analysis-overlay"
              style={{
                position: "absolute",
                // 2026-03-06 (Dave) — Chart overlay sits directly
                // below the summary rows above the year schedule.
                // Tuning history: 39 clipped the High Risk row's
                // bottom; 52 dropped so far the first year row peeked
                // through above the chart top. 45 cleared 3 band rows
                // and still hid Year 1.
                //
                // 2026-02 (Dave) — Data rows on the Contributions
                // tab were bumped from 11 → 13 px with taller
                // vertical padding. That made the block of summary
                // rows above the chart (3 risk bands + Average
                // Funding Level %) noticeably taller, so the
                // overlay `top` was raised 60 → 88 to keep clearing
                // all four rows.  `bottom` was raised 16 → 32 at
                // Dave's request to shorten the chart itself so it
                // doesn't clip against the taller year rows below.
                top: 88,
                left: 0,
                right: 0,
                // 2026-09-14 (Dave) — 16 px of breathing room at the
                // bottom (up from 4) so the panel's bottom border and
                // rounded corners never clip against the parent's
                // overflow: hidden on shorter viewports.
                // 2026-02 (Dave, v3) — Trimmed to 0. With the new
                // dynamic-max-height fit the scroll body is exactly
                // viewport-sized, so ANY bottom reservation lets year
                // rows show through below the chart border.  The
                // panel's own inner border/radius provides the visual
                // finish; no gap needed.
                bottom: 0,
                pointerEvents: "auto",
                zIndex: 5,
              }}
            >
              <ComparativeAnalysisChart
                methods={methods}
                targetPct={variables?.target_pct}
                horizon={horizon}
                unitCount={association?.unit_count || 0}
                hasAdjustment={Number(variables?.adjustment_pct || 0) !== 0}
                onDismiss={onDismissChart}
                ffbByYear={ffbByYear}
                priorYearContribution={association?.prior_year_contribution || 0}
              />
            </div>
          )}
        </div>

        {/* 2026-03-01 (Dave) — Former standalone Total table removed;
            row is now inside the year table above so it sits flush
            beneath Year 30 with no vertical gap. */}
      </div>
    </div>
  );
}

const CELL_STYLE = { textAlign: "right", paddingRight: 12 };

// 2026-03-06 (Dave) — Risk-band summary row rendered ABOVE the annual
// contribution amounts in the year-scroll table. Each cell shows the
// COUNT of years (within the horizon-context window) where THAT
// method's per-year Funding Level % lands in the given risk band.
// Palette matches the Reserve Budget Report's funding-level table:
//   • Low  Risk → pale green (#E4F5EA)
//   • Mod  Risk → pale amber (#FCF1D3)
//   • High Risk → pale red   (#FBDDD2)
// The row's background is opaque so the year-column sticky cell tints
// with the rest of its row, mirroring how the peak-year rule works.
function RiskBandRow({ label, band, byKey, cols, horizon, testid }) {
  const bandBg = band === "low" ? "#E4F5EA" : band === "mod" ? "#FCF1D3" : "#FBDDD2";
  const bandFg = band === "low" ? "#0F7A55" : band === "mod" ? "#B78900" : "#B23A1F";
  const countFor = (m) => {
    if (!m?.schedule) return 0;
    const N = Math.min(horizon, m.schedule.length);
    return m.display?.horizons?.[String(N)]?.risk_years?.[band] ?? 0; // server-computed
  };
  return (
    <tr data-testid={testid} style={{ background: bandBg }}>
      <td
        className="contrib-year-sticky"
        style={{
          paddingLeft: 12,
          fontWeight: 700,
          fontSize: 13,
          color: bandFg,
          position: "sticky",
          left: 0,
          background: bandBg,
          zIndex: 1,
          transform: "translateX(var(--outer-sx, 0))",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </td>
      {cols.map((c) => {
        const m = byKey[c.key];
        const count = m ? countFor(m) : 0;
        return (
          <td
            key={c.key}
            data-testid={`${testid}-${c.key}`}
            style={{
              ...CELL_STYLE,
              borderRight: c.dividerAfter ? "1px solid #0091D5" : undefined,
              paddingRight: c.dividerAfter ? 35 : CELL_STYLE.paddingRight,
              fontFamily: "IBM Plex Mono, monospace",
              fontVariantNumeric: "tabular-nums",
              fontWeight: 700,
              color: bandFg,
            }}
          >
            {count}
          </td>
        );
      })}
    </tr>
  );
}

// 2026-02 (Dave) — Average Funding Level %.
//
// Arithmetic mean of per-year `ending / full_funding_balance` across
// the currently-visible horizon (30 rows at 30y horizon). Renders as
// a whole-number percent (e.g. `87%`). Only populated for the columns
// listed in `shownFor` — other columns render an em-dash.
//
// Edge rules (confirmed with Dave, 2026-02):
//   • Per-year ratio is clipped at 100% (over-funded years don't get
//     "credit" past target).
//   • Years with negative ending balance are treated as 0%.
//   • Years where full_funding_balance is 0 also contribute 0% (avoid
//     div-by-zero); denominator of the mean is still the horizon
//     length so a broken plan doesn't magically inflate the average.
function AvgFundingLevelRow({ label, byKey, cols, horizon, shownFor, testid }) {
  const bg = "#F1F5FB"; // very pale navy-tinted wash so the row reads
                       // as an aggregate/summary line, distinct from
                       // both the risk-band rows above and the year
                       // rows below.
  const fg = "#1C4E80"; // uReserve navy
  const avgFor = (m) => {
    if (!m?.schedule) return null;
    const N = Math.min(horizon, m.schedule.length);
    if (N <= 0) return null;
    return m.display?.horizons?.[String(N)]?.avg_funding_pct ?? null; // server-computed
  };
  return (
    <tr data-testid={testid} style={{ background: bg }}>
      <td
        className="contrib-year-sticky"
        style={{
          paddingLeft: 12,
          fontWeight: 700,
          fontSize: 13,
          color: fg,
          position: "sticky",
          left: 0,
          background: bg,
          zIndex: 1,
          transform: "translateX(var(--outer-sx, 0))",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </td>
      {cols.map((c) => {
        const m = byKey[c.key];
        const show = shownFor.includes(c.key);
        const avg = show && m ? avgFor(m) : null;
        return (
          <td
            key={c.key}
            data-testid={`${testid}-${c.key}`}
            style={{
              ...CELL_STYLE,
              borderRight: c.dividerAfter ? "1px solid #0091D5" : undefined,
              paddingRight: c.dividerAfter ? 35 : CELL_STYLE.paddingRight,
              fontFamily: "IBM Plex Mono, monospace",
              fontVariantNumeric: "tabular-nums",
              fontWeight: 700,
              color: fg,
            }}
          >
            {avg == null ? "—" : `${Math.round(avg)}%`}
          </td>
        );
      })}
    </tr>
  );
}

function SummaryRow({ label, labelExtra, cols, byKey, getter, bold, spacer, colorful, accent, raw, flowRule, punchline, shortfall, variance, indent }) {
  // 2026-02-16 (Dave) — Summary rows are now rendered in a separate
  // <table> below the scrollable year-rows table (see JSX above), so
  // no `position: sticky` is required. The two tables share a single
  // <colgroup> + `table-layout: fixed` so columns align to the pixel.
  //
  // 2026-02-16 (Dave, coral-line polish) — the punchline row uses
  // `box-shadow: inset` (NOT border-top/bottom) for the coral hairs
  // so `border-collapse: collapse` can't lose the top rule to the
  // adjacent row's default gray border-bottom. Inset shadow paints
  // inside the cell and bypasses collapse resolution entirely.
  //
  // 2026-02-28 (Dave, v2) — thinner orange line BETWEEN the Funding
  // Level % and Target Funding Level (Shortfall) Surplus rows.  Both
  // rows share the coral-wash background, so the bottom rule of the
  // punchline row is the divider that reads to the eye.  1 px is the
  // CSS minimum, so "thinner" means visually lighter: half-opacity
  // coral for the internal separator.
  // 2026-02-28 (Dave, v3) — bottom rule of the shortfall row switched
  // from coral → navy so the paired block closes with a distinct
  // "authoritative" line beneath, and doesn't visually bleed into
  // the coral hairs above.
  const punchShadow = punchline
    ? "inset 0 1px 0 0 #EA6A47, inset 0 -1px 0 0 rgba(234,106,71,0.5)"
    : shortfall
    ? "inset 0 -1px 0 0 #1C4E80"
    : undefined;
  const rowBg = punchline || shortfall ? "var(--u-coral-wash)" : undefined;
  return (
    <tr
      data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}
      style={{
        // 2026-02-15 (Dave) — divider between the Total row and the
        // Beginning Balance / summary block. Was 2px gray (`--u-rule`);
        // Dave requested a thicker navy separator so the transition
        // from projected schedule → funded-balance summary reads
        // clearly. Only applied when `spacer` is set (i.e., first
        // summary row — Beginning Balance).
        //
        // 2026-02-15 (Dave, follow-up) — `flowRule`: a thin coral hair
        // that brackets the "flow" rows (Contributions / Interest /
        // Special Assessments / Expenditures) inside the summary block,
        // separating them visually from the surrounding "state" rows
        // (Beginning Balance, Ending Balance, Fully Funded Balance).
        // Applied to the FIRST flow row (Contributions) and the FIRST
        // return-to-state row (Ending Balance) so the eye instantly
        // parses "state → flow → state" without needing to bold-scan.
        borderTop: spacer
          ? "3px solid #1C4E80"
          : flowRule
          ? "1px solid #EA6A47"
          : undefined,
        // 2026-02-15 (Dave, follow-up 2) — `punchline`: coral wash
        // background applied to the Funding Level row (the board-facing
        // "how funded are we?" answer). Matches the peak-expenditure
        // treatment on the year-schedule rows above — coral wash +
        // 1-px coral top/bottom lines (via box-shadow per-cell below) —
        // so the eye lands on the punchline before drifting off the table.
        //
        // 2026-02-28 (Dave) — `shortfall` shares the same coral wash
        // so the Funding-Level + Shortfall rows read as one paired
        // block, then a bolder 2 px bottom rule closes the block.
        background: rowBg,
      }}
    >
      <td
        className="contrib-year-sticky"
        style={{
          // 2026-02-28 (Dave) — `indent` = child/detail rows that
          // roll up under the state rows above.  Adds ~2 monospace
          // char widths (14 px) of left padding so the hierarchy
          // reads at a glance: state (flush) → detail (indented).
          //   • Contributions
          //   • Interest Earned
          //   • Expenditures Funded From Reserves
          //   • Expenditures Funded From Special Assessments
          //   • Fully Funded Balance
          //   • Target Funding Level at X%
          paddingLeft: indent ? 26 : 12,
          fontWeight: bold ? 700 : 400,
          color: accent ? "#0091D5" : "#1C4E80",
          // 2026-02-16 (Dave, freeze-Year-col) — sticky-left so summary
          // labels stay visible on iPad horizontal scroll. Coral wash
          // for the Funding Level punchline row, white otherwise, so
          // the sticky cell always has an opaque bg matching its row.
          //
          // 2026-02-19 (Dave) — `contrib-year-sticky` className adds a
          // CSS `!important` hover override so the sticky Year/label
          // cell tints with the rest of its row (see index.css).
          position: "sticky",
          left: 0,
          background: rowBg || "#fff",
          zIndex: 1,
          boxShadow: punchShadow,
        }}
      >
        {label}
        {labelExtra}
      </td>
      {cols.map((c) => {
        const m = byKey[c.key];
        if (!m) {
          return (
            <td
              key={c.key}
              style={{
                ...CELL_STYLE,
                borderRight: c.dividerAfter ? "1px solid #0091D5" : undefined,
                paddingRight: c.dividerAfter ? 35 : CELL_STYLE.paddingRight,
                boxShadow: punchShadow,
              }}
            >
              —
            </td>
          );
        }
        const v = getter(m);
        let display;
        // 2026-02-28 (Dave) — `variance`: accounting-style signed cell.
        //   • v >= 0 → surplus, rendered in normal navy as "$n,nnn"
        //   • v <  0 → shortfall, rendered coral with parentheses "($n,nnn)"
        // Used by the Target Funding Level (Shortfall) Surplus row so the
        // sign of the number carries meaning at a glance.
        const varianceIsShortfall = variance && v < 0;
        if (raw) {
          display = v;
        } else if (variance) {
          display = fmtCurrency(v);
        } else if (colorful && v < 0) {
          display = fmtCurrency(v);
        } else {
          display = fmtCurrency(v);
        }
        return (
          <td
            key={c.key}
            style={{
              ...CELL_STYLE,
              borderRight: c.dividerAfter ? "1px solid #0091D5" : undefined,
              paddingRight: c.dividerAfter ? 35 : CELL_STYLE.paddingRight,
              fontFamily: "IBM Plex Mono, monospace",
              fontVariantNumeric: "tabular-nums",
              fontWeight: bold ? 700 : 400,
              color: varianceIsShortfall
                ? "#000000"
                : colorful
                  ? "#000000"
                  : accent
                    ? "#0091D5"
                    : bold
                      ? "#1C4E80"
                      : undefined,
              boxShadow: punchShadow,
            }}
          >
            {display}
          </td>
        );
      })}
    </tr>
  );
}
